import { Box, Stack } from '@chakra-ui/react';
import Link from 'components/Link';
import { StatusTag } from 'components/StatusTag';
import { B2CMultiSelectDropDown } from 'components/Table/B2CMultiSelectDropdown';
import useB2CVerificationsRequestQuery from 'hooks/useB2CVerificationsRequestQuery';
import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IdentificationResultIcon } from '../../components/Icons';
import { DataTable } from '../../components/Table/DataTable';
import { ContentCard } from '../../layout/ContentCard';

const B2CVerificationOverviewCard = (props: { autoFocus?: boolean }) => {
  const { autoFocus = true } = props;
  const { t } = useTranslation();
  const [filtersSelected, setFiltersSelected] = useState(false);

  const verificationCodes = { created: 'created', submitted: 'submitted', processing: 'processing', approved: 'approved', rejected: 'rejected' };

  const initialFilters = {
    searchString: '',
    itemsPerPage: 20,
    status: [],
    assignee: undefined,
    country: undefined,
  };
  const localStorageData = window.localStorage.getItem('b2c_workshop_verification_requests_filters');

  // @ts-ignore
  const destringifiedData = JSON.parse(localStorageData) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('b2c_workshop_verification_requests_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [status, setStatus] = useState(destringifiedData?.status || initialFilters?.status);
  const [assignee, setAssignee] = useState([destringifiedData?.assignee || initialFilters?.assignee]);

  const b2cVerificationsRequestQuery = useB2CVerificationsRequestQuery({
    searchString,
    page,
    itemsPerPage,
    status: status.join(',') || undefined,
  });

  // @ts-ignore
  const verifications = b2cVerificationsRequestQuery.data?.data || [];
  // @ts-ignore
  const currentPage = b2cVerificationsRequestQuery.data?.meta?.current_page - 1;

  const initialLoad = searchString === '' && itemsPerPage === 20 && page === 1 && status === undefined && isNil(assignee);

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage, status, assignee]);

  const allStatusSelected = status.length === 3;
  useEffect(() => {
    setFiltersSelected(status?.length !== 0);

    window.localStorage.setItem(
      'b2c_workshop_verification_requests_filters',
      JSON.stringify({
        searchString: searchString,
        status: status,
        assignee: assignee,
        itemsPerPage: itemsPerPage,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString, status, itemsPerPage]);

  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return <Link to={`/app/op/b2c-verification-request/${row.original.id}`}>{row.original.user_readable_id}</Link>;
        },
      },
      {
        Header: t('forms:status.label'),
        accessor: 'status',
        shouldFilter: true,
        Filter: () =>
          B2CMultiSelectDropDown(status ?? [], setStatus, 'b2c_verification', verificationCodes, allStatusSelected, t('forms:status.label')),

        // @ts-ignore
        Cell: ({ value }: { value: string }) => <StatusTag resourceKey="b2c_verification_request" currentStatus={value} isCell />,
      },
      {
        Header: t('b2c_verification:service_history.label'),
        accessor: 'b2c_service_history_request_id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Link to={`/app/op/b2c-service-history-request/${row.original.b2c_service_history_request_id}`}>
              {row.original.b2c_service_history_request_user_readable_id}
            </Link>
          );
        },
      },
      {
        Header: t('forms:assignee.label'),
        accessor: 'open_task.assignee.name',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const assignee = row?.original?.open_task?.assignee?.name;
          return <Box> {assignee ?? '-'}</Box>;
        },
      },
      {
        Header: t('workshop:email.label'),
        accessor: 'email',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const email = row?.original?.email;
          return <Box> {email ?? '-'}</Box>;
        },
      },
      {
        id: 'button',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Link to={`/app/op/b2c-verification-request/${row.original.id}`} data-test-id={'edit-' + row.index}>
              {t('common:edit')}
            </Link>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [verificationCodes, allStatusSelected, initialLoad, searchString, status, assignee, itemsPerPage, page],
  );

  return (
    <ContentCard
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('b2c_verification:header')}
      data-test-id="verifications-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w="full">
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setStatus(initialFilters?.status);
              setItemsPerPage(initialFilters?.itemsPerPage);
              setAssignee(initialFilters?.assignee ?? []);
              window.localStorage.setItem('b2c_workshop_verification_requests_filters', JSON.stringify(initialFilters));
            }}
            data-test-id="verifications-table"
            filtersSelected={filtersSelected}
            data={verifications}
            globalFilter={searchString}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={autoFocus}
            globalFilterPlaceholder={t('b2c_verification:overview.search_placeholder')}
            // @ts-ignore
            hasFilters={true}
            hasSearchInput={true}
            currentPage={currentPage}
            perPage={itemsPerPage}
            // @ts-ignore
            total={b2cVerificationsRequestQuery.data?.meta?.last_page || 1}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={b2cVerificationsRequestQuery.isFetching || b2cVerificationsRequestQuery.isLoading}
            // @ts-ignore
            isMoreThenOnePage={!initialLoad || Boolean(b2cVerificationsRequestQuery.data?.meta?.next_page_url) || currentPage > 0}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default B2CVerificationOverviewCard;
